import React, { memo, useEffect, useRef, useState } from "react";
import {
  Box,
  Grid as MuiGrid,
  Accordion,
  AccordionDetails,
  Typography as MuiTypography,
  Tooltip,
  withWidth,
  isWidthDown,
} from "@material-ui/core";
import styled, { keyframes } from "styled-components";
import axios from "axios";
import { spacing } from "@material-ui/system";
import { lineColors } from "../../../utils";
import DatePicker from "../../../components/pickers/DatePicker";
import Panel from "../../../components/panels/Panel";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Button from "@material-ui/core/Button";
import ExportDataButton from "../../../components/graphs/ExportDataButton";
import SaveRefButton from "../../../components/graphs/SaveRefButton";
import IconButton from "@material-ui/core/IconButton";
import { Close, Room } from "@material-ui/icons";
import ExpandButton from "../../../components/graphs/ExpandButton";
import TimeseriesLineChartDataViz from "../../../components/graphs/TimeseriesLineChartDataViz";
import { add } from "date-fns";
import { isWidthUp } from "@material-ui/core/withWidth";

const TruncatedTypographyTitle = styled(MuiTypography)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const fadeIn = keyframes`
  from {
    transform: scale(.25);
    opacity: 0;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    transform: scale(1);
    opacity: 0;
  }

  to {
    transform: scale(.25);
    opacity: 1;
  }
`;

const OuterContainer = styled(Box)`
  margin-left: ${({ dynamicleftmargin }) => dynamicleftmargin};
  bottom: ${({ dynamicbottommargin }) => dynamicbottommargin};
  z-index: 3;
  position: absolute;
  max-height: 100%;
  width: ${({ dynamicleftmargin }) =>
    `calc(100% - ${dynamicleftmargin} - ${dynamicleftmargin})`};
  visibility: ${({ open }) => (open ? "visible" : "hidden")};
  animation: ${({ open }) => (open ? fadeIn : fadeOut)} 0.5s linear;
  transition: visibility 0.5s linear;
`;

const Viz = styled.div`
  height: ${({ height }) => height};
  max-width: 100%;
`;

const TimeseriesWrapper = styled.div`
  height: calc(100% - 78px);
  width: 100%;
`;

const TimeseriesContainer = styled.div`
  height: calc(${({ height }) => height} - 146px);
  width: 100%;
`;

const CircleMarker = styled.div`
  text-align: center;
  border-radius: 50%;
  color: white;
  background-color: ${({ theme }) => theme.palette.primary.main};
  width: 50px;
  height: 50px;
  line-height: 66px;
  margin-right: 13px;
`;

const CloseContainer = styled.div`
  // position: absolute;
  display: flex;
  justify-content: end;
  margin-top: 5px;
  margin-right: 5px;
  margin-bottom: -10px;
`;

const Grid = styled(MuiGrid)(spacing);
const Typography = styled(MuiTypography)(spacing);

const DataViz = ({
  width,
  open = false,
  dataVizWellNumber,
  dataVizGraphType,
  onClose,
}) => {
  const divSaveRef = useRef(null);
  const graphSaveRef = useRef(null);

  //date filter defaults
  const defaultFilterValues = {
    // startDate: lastOfJanuary,
    startDate: add(new Date(), { days: -3 }),
    endDate: new Date(),
  };

  // `filterValues` holds the raw picker input (may be mid-typed / invalid).
  // `appliedRange` is the last valid range actually queried + drawn — the chart
  // and export always read from this, so a half-typed date can never break the
  // graph or the x-axis.
  const [filterValues, setFilterValues] = useState(defaultFilterValues);
  const [appliedRange, setAppliedRange] = useState(defaultFilterValues);
  const changeFilterValues = (name, value) => {
    setFilterValues((prevState) => {
      let newFilterValues = { ...prevState };
      newFilterValues[name] = value;
      return newFilterValues;
    });
  };

  const [currentSelectedTimeseriesData, setCurrentSelectedTimeseriesData] =
    useState(null);
  const [isFetching, setIsFetching] = useState(false);
  useEffect(() => {
    if (!dataVizWellNumber || !dataVizGraphType) return;

    // Only query once a complete, valid range is entered. Incomplete or invalid
    // input (mid-typing, or end date before start date) is ignored, leaving the
    // current graph and — crucially — the filter controls in place so it can be
    // corrected.
    const startDate = filterValues.startDate;
    const endDate = filterValues.endDate;
    const isValidRange =
      startDate instanceof Date &&
      !isNaN(startDate.getTime()) &&
      endDate instanceof Date &&
      !isNaN(endDate.getTime()) &&
      startDate <= endDate;
    if (!isValidRange) return;

    // Guards against a stale (slower) response overwriting a newer one.
    let active = true;
    // Debounce so typing a date doesn't fire a request on every keystroke.
    const timer = setTimeout(() => {
      async function send() {
        try {
          const endpoint = {
            has_flow_graph: "graph-flow-data15-min",
            has_stage_graph: "graph-stage-data15-min",
            has_waterlevel_graph: "graph-waterlevel-data15-min",
          };

          setIsFetching(true);

          // Only fetch the selected window instead of the site's full history.
          const { data: results } = await axios.post(
            `${process.env.REACT_APP_ENDPOINT}/api/${endpoint[dataVizGraphType]}/${dataVizWellNumber}`,
            { startDate, endDate }
          );

          if (!active) return;

          setAppliedRange({ startDate, endDate });
          setCurrentSelectedTimeseriesData(results.length ? results : null);
        } catch (err) {
          // Is this error because we cancelled it ourselves?
          if (axios.isCancel(err)) {
            console.log(`call was cancelled`);
          } else {
            console.error(err);
          }
        } finally {
          if (active) setIsFetching(false);
        }
      }
      send();
    }, 500);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [
    dataVizWellNumber,
    dataVizGraphType,
    filterValues.startDate,
    filterValues.endDate,
  ]);

  const [filteredMutatedGraphData, setFilteredMutatedGraphData] = useState({});
  useEffect(() => {
    if (currentSelectedTimeseriesData?.length) {
      //mutate data for chartJS to use
      let graphData;
      if (dataVizGraphType === "has_stage_graph") {
        graphData = {
          labels: currentSelectedTimeseriesData.map(
            (item) => new Date(item.collect_timestamp)
          ),
          datasets: [
            {
              label: "Stage",
              units: currentSelectedTimeseriesData[0].units,
              type: "line",
              yAxisID: "yL",
              pointStyle: "point",
              backgroundColor: lineColors.blue,
              borderColor: lineColors.blue,
              data: currentSelectedTimeseriesData.map((item) => item.stage_ft),
              popupInfo: currentSelectedTimeseriesData.map(
                (item) => item.source
              ),
              pointRadius: 2,
              pointHoverRadius: 4,
              borderWidth: 3,
              spanGaps: false,
              hidden: false,
            },
          ],
        };
      } else if (dataVizGraphType === "has_flow_graph") {
        graphData = {
          labels: currentSelectedTimeseriesData.map(
            (item) => new Date(item.collect_timestamp)
          ),
          datasets: [
            {
              label: "Flow",
              units: currentSelectedTimeseriesData[0].units,
              type: "line",
              yAxisID: "yL",
              pointStyle: "point",
              backgroundColor: lineColors.blue,
              borderColor: lineColors.blue,
              data: currentSelectedTimeseriesData.map((item) => item.flow_cfs),
              popupInfo: currentSelectedTimeseriesData.map(
                (item) => item.source
              ),
              pointRadius: 2,
              pointHoverRadius: 4,
              borderWidth: 3,
              spanGaps: false,
              hidden: false,
            },
          ],
        };
      } else if (dataVizGraphType === "has_waterlevel_graph") {
        graphData = {
          labels: currentSelectedTimeseriesData.map(
            (item) => new Date(item.collect_timestamp)
          ),
          datasets: [
            {
              label: "Water Level",
              units: currentSelectedTimeseriesData[0].units,
              type: "line",
              yAxisID: "yL",
              pointStyle: "point",
              backgroundColor: lineColors.blue,
              borderColor: lineColors.blue,
              data: currentSelectedTimeseriesData.map((item) => item.level_ft),
              popupInfo: currentSelectedTimeseriesData.map(
                (item) => item.source
              ),
              pointRadius: 2,
              pointHoverRadius: 4,
              borderWidth: 3,
              spanGaps: false,
              hidden: false,
            },
          ],
        };
      }
      setFilteredMutatedGraphData(graphData);
    } else {
      setFilteredMutatedGraphData(null);
    }
  }, [currentSelectedTimeseriesData, dataVizGraphType]);

  const formatTableTitle = (graphType) => {
    if (
      ["has_flow_graph", "has_stage_graph", "has_waterlevel_graph"].includes(
        graphType
      )
    ) {
      return (
        <>
          <TruncatedTypographyTitle
            variant={isWidthDown("sm", width) ? "h4" : "h3"}
            style={{ lineHeight: 1.3 }}
          >
            <strong>
              {currentSelectedTimeseriesData[0].location_name ?? "NA"}
            </strong>
          </TruncatedTypographyTitle>
          <Typography variant="subtitle1" style={{ lineHeight: 1.3 }}>
            <Box>{currentSelectedTimeseriesData[0].location_id ?? "NA"}</Box>
          </Typography>
        </>
      );
    } else {
      return null;
    }
  };

  const [dataVizHeight, setDataVizHeight] = useState({
    viz: isWidthDown("sm", width) ? "360px" : "460px",
    timeSeries: isWidthDown("sm", width) ? "465px" : "500px",
  });

  const handleExpand = () => {
    let newState = { ...dataVizHeight };
    if (
      ["460px", "360px"].includes(newState.viz) &&
      ["465px", "500px"].includes(newState.timeSeries)
    ) {
      newState.viz = isWidthDown("sm", width) ? "60vh" : "70vh";
      newState.timeSeries = isWidthDown("sm", width) ? "600px" : "860px";
    } else {
      newState.viz = isWidthDown("sm", width) ? "360px" : "460px";
      newState.timeSeries = isWidthDown("sm", width) ? "465px" : "500px";
    }
    setDataVizHeight(newState);
  };

  const adjustHeight = () => {
    let newState = { ...dataVizHeight };
    if (
      ["460px", "360px"].includes(newState.viz) &&
      ["465px", "500px"].includes(newState.timeSeries)
    ) {
      newState.viz = isWidthDown("sm", width) ? "360px" : "460px";
      newState.timeSeries = isWidthDown("sm", width) ? "465px" : "500px";
    } else {
      newState.viz = isWidthDown("sm", width) ? "60vh" : "70vh";
      newState.timeSeries = isWidthDown("sm", width) ? "600px" : "860px";
    }
    setDataVizHeight(newState);
  };

  useEffect(() => {
    adjustHeight();
  }, [width]); //eslint-disable-line

  const hasData =
    !!filteredMutatedGraphData &&
    Array.isArray(currentSelectedTimeseriesData) &&
    currentSelectedTimeseriesData.length > 0;

  return (
    <OuterContainer
      bgcolor="#ffffff"
      boxShadow="0 0 0 2px rgba(0,0,0,.1)"
      borderRadius={4}
      open={open}
      dynamicleftmargin={isWidthUp("sm", width) ? "49px" : "10px"}
      dynamicbottommargin={isWidthUp("sm", width) ? "49px" : "28px"}
    >
      <Viz height={dataVizHeight.viz}>
        <CloseContainer>
          <ExpandButton
            handleExpand={handleExpand}
            expanded={!["460px", "360px"].includes(dataVizHeight.viz)}
          />
          <Tooltip title="Close" arrow>
            <IconButton
              size="small"
              onClick={onClose}
              style={{ marginLeft: "4px" }}
            >
              <Close />
            </IconButton>
          </Tooltip>
        </CloseContainer>
        {dataVizWellNumber && dataVizGraphType && (
          <Panel overflowY="scroll" overflowX="hidden">
            <TimeseriesContainer
              height={dataVizHeight.timeSeries}
              ref={divSaveRef}
            >
              <Box ml={4} pt={2} pb={2} display="flex" alignItems="center">
                {isWidthUp("sm", width) && (
                  <CircleMarker>
                    <Room />
                  </CircleMarker>
                )}
                <Box flexDirection="column" display="flex">
                  {hasData && formatTableTitle(dataVizGraphType)}
                </Box>

                {hasData && (
                  <Box
                    style={{ marginLeft: "auto" }}
                    data-html2canvas-ignore={"true"}
                    display="flex"
                  >
                    <ExportDataButton
                      title="well_ndx"
                      data={currentSelectedTimeseriesData}
                      filterValues={appliedRange}
                    />
                    <SaveRefButton
                      data-html2canvas-ignore
                      ref={divSaveRef}
                      title={dataVizWellNumber}
                    />
                  </Box>
                )}
              </Box>

              <TimeseriesWrapper>
                {hasData ? (
                  <TimeseriesLineChartDataViz
                    data={filteredMutatedGraphData}
                    yLLabel={
                      dataVizGraphType === "has_flow_graph"
                        ? `Flow, ${currentSelectedTimeseriesData[0].units}`
                        : dataVizGraphType === "has_stage_graph"
                        ? `Stage, ${currentSelectedTimeseriesData[0].units}`
                        : dataVizGraphType === "has_waterlevel_graph"
                        ? `Water Level, ${currentSelectedTimeseriesData[0].units}`
                        : "N/A"
                    }
                    xLabelFormat="MMM DD YYYY"
                    reverseLegend={true}
                    enableLegendClick={true}
                    ref={graphSaveRef}
                    tooltipFormat="MM-DD-YYYY, h:mm A"
                    filterValues={appliedRange}
                    type="line"
                    displayLegend={true}
                    xLabelUnit="day"
                    maxTicksX={isWidthUp("sm", width) ? 12 : 6}
                    maxTicksYL={6}
                    maxTicksYR={5}
                    align="start"
                  />
                ) : (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                  >
                    <Typography variant="subtitle1" color="textSecondary">
                      {isFetching
                        ? "Loading…"
                        : "No data available for the selected date range."}
                    </Typography>
                  </Box>
                )}
              </TimeseriesWrapper>
            </TimeseriesContainer>

            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="map"
                id="map"
              >
                <Typography variant="h4">Filters</Typography>
              </AccordionSummary>
              <AccordionDetails style={{ display: "block" }}>
                <Grid container spacing={6} alignItems="center">
                  <Grid item xs={12} sm={6} md={5}>
                    <DatePicker
                      size={isWidthDown("xs", width) ? "small" : "medium"}
                      label="Start Date"
                      name="startDate"
                      selectedDate={filterValues.startDate}
                      setSelectedDate={changeFilterValues}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={5}>
                    <DatePicker
                      size={isWidthDown("xs", width) ? "small" : "medium"}
                      label="End Date"
                      name="endDate"
                      selectedDate={filterValues.endDate}
                      setSelectedDate={changeFilterValues}
                    />
                  </Grid>

                  <Grid item xs={12} sm={12} md={2}>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={6} md={12}>
                        <Tooltip title="Last 30 Days">
                          <Button
                            size="small"
                            style={{ width: "100%" }}
                            variant="outlined"
                            color="primary"
                            onClick={() => {
                              changeFilterValues(
                                "startDate",
                                add(new Date(), { days: -30 })
                              );
                              changeFilterValues("endDate", new Date());
                            }}
                          >
                            Last 30 Days
                          </Button>
                        </Tooltip>
                      </Grid>
                      <Grid item xs={6} sm={6} md={12}>
                        <Tooltip title="Last 12 Months" placement="bottom">
                          <Button
                            size="small"
                            style={{ width: "100%" }}
                            variant="outlined"
                            color="primary"
                            onClick={() => {
                              changeFilterValues(
                                "startDate",
                                add(new Date(), { months: -12 })
                              );
                              changeFilterValues("endDate", new Date());
                            }}
                          >
                            Last 12 Months
                          </Button>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Panel>
        )}
      </Viz>
    </OuterContainer>
  );
};

// Memoized so the graph is decoupled from the public map's frequent re-renders.
// It only re-renders when the selected site/graph actually changes (or when open
// toggles); `onClose` is an inline callback from the parent and is intentionally
// excluded from the comparison so a new function ref each map render doesn't
// force the chart to rebuild. Width changes still re-render via the withWidth HOC.
const DataVizMemo = memo(
  withWidth()(DataViz),
  (prev, next) =>
    prev.open === next.open &&
    prev.dataVizWellNumber === next.dataVizWellNumber &&
    prev.dataVizGraphType === next.dataVizGraphType
);

export default DataVizMemo;
